import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { traiComplianceService, A2pCategory, CATEGORY_OPTIONS } from 'api/services/traiCompliance.service';
import { isBuildFeatureOn } from 'components/Layout/navVisibility';

export const TRAI_FLAG = 'trai-compliance';

/** Behind REACT_APP_ENABLE_FEATURES=trai-compliance; a normal build renders nothing. */
export const traiComplianceOn = (): boolean => isBuildFeatureOn(TRAI_FLAG);

const HINT =
  'What these calls are for under TRAI\'s A2P rules. Recorded and reported only — no call is refused because of it.';

/** A bare select for a form that sends the value itself (campaign create). Empty = not set. */
export const CallCategorySelect: React.FC<{
  value: '' | A2pCategory;
  onChange: (v: '' | A2pCategory) => void;
  className?: string;
  id?: string;
}> = ({ value, onChange, className, id }) => (
  <select id={id} className={className} value={value} onChange={(e) => onChange(e.target.value as '' | A2pCategory)}>
    {CATEGORY_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

/**
 * A select that loads and saves its own default — for an existing agent or campaign.
 * Saving an empty value clears the default (today's behaviour: the call is unclassified).
 */
const CallCategoryField: React.FC<{
  scope: 'agent' | 'campaign';
  id: string | number;
  canEdit: boolean;
  label?: string;
}> = ({ scope, id, canEdit, label = 'Call category (TRAI)' }) => {
  const [value, setValue] = useState<'' | A2pCategory>('');
  const [saved, setSaved] = useState<'' | A2pCategory>('');
  const [orgDefault, setOrgDefault] = useState<A2pCategory | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    let alive = true;
    traiComplianceService.categories().then((r) => {
      if (!alive) return;
      if (r.success && r.data) {
        const map = scope === 'agent' ? r.data.agents : r.data.campaigns;
        const v = (map?.[String(id)] || '') as '' | A2pCategory;
        setValue(v); setSaved(v);
        setOrgDefault(r.data.orgDefault);
      } else {
        setNote({ ok: false, text: r.error || 'Could not load the category' });
      }
      setLoading(false);
    });
    return () => { alive = false; };
  }, [scope, id]);

  const save = async () => {
    setBusy(true); setNote(null);
    const r = scope === 'agent'
      ? await traiComplianceService.setAgentCategory(id, value || null)
      : await traiComplianceService.setCampaignCategory(String(id), value || null);
    setBusy(false);
    if (r.success) { setSaved(value); setNote({ ok: true, text: value ? 'Saved' : 'Cleared' }); }
    else setNote({ ok: false, text: r.error || 'Could not save' });
  };

  if (loading) return <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Loading call category…</div>;

  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">{label}</label>
      <div className="flex items-center gap-2">
        <CallCategorySelect
          value={value}
          onChange={setValue}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl text-sm"
        />
        {canEdit && (
          <button type="button" onClick={save} disabled={busy || value === saved} className="tl-btn tl-btn-secondary tl-btn-sm disabled:opacity-50">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
          </button>
        )}
      </div>
      <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
        {HINT}{!value && orgDefault ? ` Unset here, so the organisation default applies: ${orgDefault.toLowerCase()}.` : ''}
      </p>
      {note && <p className={`text-[11px] ${note.ok ? 'text-green-700' : 'text-red-700'}`}>{note.text}</p>}
    </div>
  );
};

export default CallCategoryField;
